import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Widgets from "../../../../widgets";
import strings from "../../../../utils/strings";
import Icons from "../../../../Assets/Icons";
import "./EditZoneFee.css";
import {
  fetchTransportationFee,
  updateTransportationFee,
} from "../../../../modules/NursignCare/TransportationFee/transportationFeeThunk";

const stripCommas = (v) => String(v || "").replace(/,/g, "");

const formatWithCommas = (raw) => {
  const onlyDigits = String(raw || "").replace(/\D/g, "");
  if (!onlyDigits) return "";
  return onlyDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};


const cloneDeep = (obj) => {
  if (!obj) return obj;
  return JSON.parse(JSON.stringify(obj));
};

const getNumberError = (value, { requiredMsg, numberMsg, min = 0 } = {}) => {
  const raw = stripCommas(value);
  if (!raw) return requiredMsg;
  const num = Number(raw);
  if (!Number.isFinite(num)) return numberMsg;
  if (num < min) return numberMsg;
  return null;
};



const EditZoneFee = () => {
  const { t } = useTranslation();
  const nav = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { fee } = useSelector((state) => state.transportationFee);
  const [initialData, setInitialData] = useState(null);

  const type = location.state?.type; // baseFare / perKmRate

  const breadcrumbs = ["nursingCare", "transportationFee", "zoneFeeDetails"];
  const paths = [
    strings.navigation.manageNurseReq,
    strings.navigation.manageTranspotationFees,
    location.pathname,
  ];

  const [motoSame, setMotoSame] = useState(false);
  const [taxiSame, setTaxiSame] = useState(false);

  const [motoWorking, setMotoWorking] = useState({ intl: "", local: "", subsidized: "" });
  const [motoNonWorking, setMotoNonWorking] = useState({ intl: "", local: "", subsidized: "" });

  const [taxiWorking, setTaxiWorking] = useState({ intl: "", local: "", subsidized: "" });
  const [taxiNonWorking, setTaxiNonWorking] = useState({ intl: "", local: "", subsidized: "" });

  const [hasPrefilled, setHasPrefilled] = useState(false);
  const [errors, setErrors] = useState({
    motoWorking: {},
    motoNonWorking: {},
    taxiWorking: {},
    taxiNonWorking: {},
  });

  useEffect(() => {
    dispatch(fetchTransportationFee());
  }, [dispatch]);

  useEffect(() => {
    setHasPrefilled(false);
  }, [type]);


  useEffect(() => {
    if (hasPrefilled) return;
    if (!fee?.transportFees || !type) return;

    const fill = (mode, setWorking, setNonWorking) => {
      const data = fee?.transportFees?.[mode];

      const getVal = (section, opd) =>
        data?.[section]?.[opd]?.[type] ?? "";

      const working = {
        intl: formatWithCommas(getVal("workingHours", "opdInternationalInsured")),
        local: formatWithCommas(getVal("workingHours", "opdLocalInsured")),
        subsidized: formatWithCommas(getVal("workingHours", "opdSubsidized")),
      };

      const nonWorking = {
        intl: formatWithCommas(getVal("nonWorkingHours", "opdInternationalInsured")),
        local: formatWithCommas(getVal("nonWorkingHours", "opdLocalInsured")),
        subsidized: formatWithCommas(getVal("nonWorkingHours", "opdSubsidized")),
      };

      setWorking(working);
      setNonWorking(nonWorking);
    };

      fill("motorbike", setMotoWorking, setMotoNonWorking);
fill("taxi", setTaxiWorking, setTaxiNonWorking);

// ✅ SAVE INITIAL STATE HERE
setInitialData({
  motoWorking: {
    intl: formatWithCommas(fee.transportFees.motorbike.workingHours.opdInternationalInsured[type]),
    local: formatWithCommas(fee.transportFees.motorbike.workingHours.opdLocalInsured[type]),
    subsidized: formatWithCommas(fee.transportFees.motorbike.workingHours.opdSubsidized[type]),
  },
  motoNonWorking: {
    intl: formatWithCommas(fee.transportFees.motorbike.nonWorkingHours.opdInternationalInsured[type]),
    local: formatWithCommas(fee.transportFees.motorbike.nonWorkingHours.opdLocalInsured[type]),
    subsidized: formatWithCommas(fee.transportFees.motorbike.nonWorkingHours.opdSubsidized[type]),
  },
  taxiWorking: {
    intl: formatWithCommas(fee.transportFees.taxi.workingHours.opdInternationalInsured[type]),
    local: formatWithCommas(fee.transportFees.taxi.workingHours.opdLocalInsured[type]),
    subsidized: formatWithCommas(fee.transportFees.taxi.workingHours.opdSubsidized[type]),
  },
  taxiNonWorking: {
    intl: formatWithCommas(fee.transportFees.taxi.nonWorkingHours.opdInternationalInsured[type]),
    local: formatWithCommas(fee.transportFees.taxi.nonWorkingHours.opdLocalInsured[type]),
    subsidized: formatWithCommas(fee.transportFees.taxi.nonWorkingHours.opdSubsidized[type]),
  },
});

setHasPrefilled(true);
  }, [fee, hasPrefilled, type]);

  const handleMoneyChange = (groupKey, setter) => (e) => {
    const { name, value } = e.target;
    setter((prev) => ({
      ...prev,
      [name]: formatWithCommas(value),
    }));
    setErrors((prev) => ({
      ...prev,
      [groupKey]: {
        ...(prev?.[groupKey] || {}),
        [name]: null,
      },
    }));
  };

  const toggleSameAsWorking = (mode) => {
    if (mode === "motobike") {
      setMotoSame((prev) => {
        const next = !prev;
        if (next) {
          setMotoNonWorking({ ...motoWorking });
        }
        return next;
      });
    } else {
      setTaxiSame((prev) => {
        const next = !prev;
        if (next) {
          setTaxiNonWorking({ ...taxiWorking });
        }
        return next;
      });
    }
  };

  const validationMessages = useMemo(() => {
    const requiredMsg = t("transportationFee.validationRequired") || "This field is required";
    const numberMsg = t("transportationFee.validationNumber") || "Enter valid number";
    return { requiredMsg, numberMsg };
  }, [t]);

  const isValid = useMemo(() => {
    const requiredMsg = validationMessages.requiredMsg;
    const numberMsg = validationMessages.numberMsg;

    const validateGroup = (group) => {
      return ["intl", "local", "subsidized"].every((k) => {
        return !getNumberError(group?.[k], { requiredMsg, numberMsg, min: 0 });
      });
    };

    const effectiveMotoNonWorking = motoSame ? motoWorking : motoNonWorking;
    const effectiveTaxiNonWorking = taxiSame ? taxiWorking : taxiNonWorking;

    const firstKmNum = Number(fee?.firstKm);
    if (!Number.isFinite(firstKmNum) || firstKmNum <= 0) return false;

    return (
      validateGroup(motoWorking) &&
      validateGroup(effectiveMotoNonWorking) &&
      validateGroup(taxiWorking) &&
      validateGroup(effectiveTaxiNonWorking)
    );
  }, [
    fee?.firstKm,
    motoNonWorking,
    motoSame,
    motoWorking,
    taxiNonWorking,
    taxiSame,
    taxiWorking,
    validationMessages.numberMsg,
    validationMessages.requiredMsg,
  ]);

  const isChanged = useMemo(() => {
  if (!initialData) return false;

  return JSON.stringify({
    motoWorking,
    motoNonWorking,
    taxiWorking,
    taxiNonWorking,
  }) !== JSON.stringify(initialData);
}, [motoWorking, motoNonWorking, taxiWorking, taxiNonWorking, initialData]);

  const handleSubmit = async () => {
    if (!fee?.transportFees || !type) return;

    const requiredMsg = validationMessages.requiredMsg;
    const numberMsg = validationMessages.numberMsg;

    const nextErrors = {
      motoWorking: {},
      motoNonWorking: {},
      taxiWorking: {},
      taxiNonWorking: {},
    };

    const validateGroup = (group, out) => {
      ["intl", "local", "subsidized"].forEach((k) => {
        const err = getNumberError(group?.[k], { requiredMsg, numberMsg, min: 0 });
        if (err) out[k] = err;
      });
    };

    validateGroup(motoWorking, nextErrors.motoWorking);
    if (!motoSame) validateGroup(motoNonWorking, nextErrors.motoNonWorking);
    validateGroup(taxiWorking, nextErrors.taxiWorking);
    if (!taxiSame) validateGroup(taxiNonWorking, nextErrors.taxiNonWorking);

    setErrors(nextErrors);

    const firstKmNum = Number(fee?.firstKm);
    if (!Number.isFinite(firstKmNum) || firstKmNum <= 0) {
      Widgets.ToastPopup.error(numberMsg);
      return;
    }

    if (!isValid) {
  Widgets.ToastPopup.error(
    t("transportationFee.validationRequired") || "Please fill all fields correctly"
  );
  return;
}

    const updated = cloneDeep(fee.transportFees);

    const updateMode = (mode, working, nonWorking) => {
      const keyMap = ["intl", "local", "subsidized"];
      const opdKeys = [
        "opdInternationalInsured",
        "opdLocalInsured",
        "opdSubsidized",
      ];

      opdKeys.forEach((opd, i) => {
        const workNum = Number(stripCommas(working[keyMap[i]]));
        const nonWorkNum = Number(stripCommas(nonWorking[keyMap[i]]));

        updated[mode].workingHours[opd] = {
          ...(updated?.[mode]?.workingHours?.[opd] || { baseFare: 0, perKmRate: 0 }),
          [type]: workNum,
        };

        updated[mode].nonWorkingHours[opd] = {
          ...(updated?.[mode]?.nonWorkingHours?.[opd] || { baseFare: 0, perKmRate: 0 }),
          [type]: nonWorkNum,
        };
      });
    };

    const effectiveMotoNonWorking = motoSame ? motoWorking : motoNonWorking;
    const effectiveTaxiNonWorking = taxiSame ? taxiWorking : taxiNonWorking;

    updateMode("motorbike", motoWorking, effectiveMotoNonWorking);
    updateMode("taxi", taxiWorking, effectiveTaxiNonWorking);

    const body = {
      firstKm: firstKmNum,
      transportFees: updated,
    };

    try {
      await dispatch(updateTransportationFee(body)).unwrap();
      Widgets.ToastPopup.success(
        t("transportationFee.successUpdate") ||
          "Transportation fee updated successfully"
      );
      nav(strings.navigation.manageTranspotationFees);
    } catch (err) {
      Widgets.ToastPopup.error(
        err ||
          t("transportationFee.errorUpdate") ||
          "Failed to update transportation fee"
      );
    }
  };

  return (
    <div className="mr-container">
      <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />

      <div className="znf-heading">
        {type === "baseFare" ? t("baseFareFees") : t("distancePerKmFees")}
      </div>

      <div className="znf-card">

        {/* MOTOBIKE */}
        <div className="znf-mode">
          <div className="znf-mode-header">
            <div className="znf-mode-name">
              <img src={Icons.Location} alt="" />
              <span>{t("motobikeFees")}</span>
            </div>
          </div>

          <div className="whp-container znf-block">
            <div className="title-hr">
              <p>{t("workingHoursLabel")}</p>
              <hr />
            </div>
            <div className="service-type-main2">
              
          <div className="service-type-main2">
  <div className="service-input-box">
    <span>{t("opdInternationalInsured")}</span>
    <div className="znf-money">
      <span>₫</span>
      <input
        type="text"
        name="intl"
        value={motoWorking.intl}
        onChange={handleMoneyChange("motoWorking", setMotoWorking)}
      />
    </div>
    {errors?.motoWorking?.intl && (
      <span className="error-message">{errors.motoWorking.intl}</span>
    )}
  </div>

  <div className="service-input-box">
    <span>{t("opdLocalInsured")}</span>
    <div className="znf-money">
      <span>₫</span>
      <input
        type="text"
        name="local"
        value={motoWorking.local}
        onChange={handleMoneyChange("motoWorking", setMotoWorking)}
      />
    </div>
    {errors?.motoWorking?.local && (
      <span className="error-message">{errors.motoWorking.local}</span>
    )}
  </div>

  <div className="service-input-box">
    <span>{t("opdSubsidized")}</span>
    <div className="znf-money">
      <span>₫</span>
      <input
        type="text"
        name="subsidized"
        value={motoWorking.subsidized}
        onChange={handleMoneyChange("motoWorking", setMotoWorking)}
      />
    </div>
    {errors?.motoWorking?.subsidized && (
      <span className="error-message">{errors.motoWorking.subsidized}</span>
    )}
  </div>
</div>
            </div>
          </div>

          <div className="whp-container znf-block">
            <div className="title-hr2">
              <p>{t("nonWorkingHoursLabel")}</p>
              <hr />
            </div>

            <button
              type="button"
              className={`clickable-text znf-same-link ${motoSame ? "active" : ""}`}
              onClick={() => toggleSameAsWorking("motobike")}
            >
              {t("sameAsWorkingHours")}
            </button>
          
          <div className="service-type-main2">
  <div className="service-input-box">
    <span>{t("opdInternationalInsured")}</span>
    <div className="znf-money">
      <span>₫</span>
      <input
        type="text"
        name="intl"
        value={motoSame ? motoWorking.intl : motoNonWorking.intl}
        onChange={handleMoneyChange("motoNonWorking", setMotoNonWorking)}
        disabled={motoSame}
      />
    </div>
    {!motoSame && errors?.motoNonWorking?.intl && (
      <span className="error-message">{errors.motoNonWorking.intl}</span>
    )}
  </div>

  <div className="service-input-box">
    <span>{t("opdLocalInsured")}</span>
    <div className="znf-money">
      <span>₫</span>
      <input
        type="text"
        name="local"
        value={motoSame ? motoWorking.local : motoNonWorking.local}
        onChange={handleMoneyChange("motoNonWorking", setMotoNonWorking)}
        disabled={motoSame}
      />
    </div>
    {!motoSame && errors?.motoNonWorking?.local && (
      <span className="error-message">{errors.motoNonWorking.local}</span>
    )}
  </div>

  <div className="service-input-box">
    <span>{t("opdSubsidized")}</span>
    <div className="znf-money">
      <span>₫</span>
      <input
        type="text"
        name="subsidized"
        value={motoSame ? motoWorking.subsidized : motoNonWorking.subsidized}
        onChange={handleMoneyChange("motoNonWorking", setMotoNonWorking)}
        disabled={motoSame}
      />
    </div>
    {!motoSame && errors?.motoNonWorking?.subsidized && (
      <span className="error-message">{errors.motoNonWorking.subsidized}</span>
    )}
  </div>
</div>
          </div>
        </div>

        {/* TAXI */}
        <div className="znf-mode">
          <div className="znf-mode-header">
            <div className="znf-mode-name">
              <img src={Icons.Location} alt="" />
              <span>{t("taxiFees")}</span>
            </div>
          </div>

          <div className="whp-container znf-block">
            <div className="title-hr">
              <p>{t("workingHoursLabel")}</p>
              <hr />
            </div>
<div className="service-type-main2">
  <div className="service-input-box">
    <span>{t("opdInternationalInsured")}</span>
    <div className="znf-money">
      <span>₫</span>
      <input
        type="text"
        name="intl"
        value={taxiWorking.intl}
        onChange={handleMoneyChange("taxiWorking", setTaxiWorking)}
      />
    </div>
    {errors?.taxiWorking?.intl && (
      <span className="error-message">{errors.taxiWorking.intl}</span>
    )}
  </div>

  <div className="service-input-box">
    <span>{t("opdLocalInsured")}</span>
    <div className="znf-money">
      <span>₫</span>
      <input
        type="text"
        name="local"
        value={taxiWorking.local}
        onChange={handleMoneyChange("taxiWorking", setTaxiWorking)}
      />
    </div>
    {errors?.taxiWorking?.local && (
      <span className="error-message">{errors.taxiWorking.local}</span>
    )}
  </div>

  <div className="service-input-box">
    <span>{t("opdSubsidized")}</span>
    <div className="znf-money">
      <span>₫</span>
      <input
        type="text"
        name="subsidized"
        value={taxiWorking.subsidized}
        onChange={handleMoneyChange("taxiWorking", setTaxiWorking)}
      />
    </div>
    {errors?.taxiWorking?.subsidized && (
      <span className="error-message">{errors.taxiWorking.subsidized}</span>
    )}
  </div>
</div>
          </div>

          <div className="whp-container znf-block">
            <div className="title-hr2">
              <p>{t("nonWorkingHoursLabel")}</p>
              <hr />
            </div>

            <button
              type="button"
              className={`clickable-text znf-same-link ${taxiSame ? "active" : ""}`}
              onClick={() => toggleSameAsWorking("taxi")}
            >
              {t("sameAsWorkingHours")}
            </button>

          <div className="service-type-main2">
  <div className="service-input-box">
    <span>{t("opdInternationalInsured")}</span>
    <div className="znf-money">
      <span>₫</span>
      <input
        type="text"
        name="intl"
        value={taxiSame ? taxiWorking.intl : taxiNonWorking.intl}
        onChange={handleMoneyChange("taxiNonWorking", setTaxiNonWorking)}
        disabled={taxiSame}
      />
    </div>
    {!taxiSame && errors?.taxiNonWorking?.intl && (
      <span className="error-message">{errors.taxiNonWorking.intl}</span>
    )}
  </div>

  <div className="service-input-box">
    <span>{t("opdLocalInsured")}</span>
    <div className="znf-money">
      <span>₫</span>
      <input
        type="text"
        name="local"
        value={taxiSame ? taxiWorking.local : taxiNonWorking.local}
        onChange={handleMoneyChange("taxiNonWorking", setTaxiNonWorking)}
        disabled={taxiSame}
      />
    </div>
    {!taxiSame && errors?.taxiNonWorking?.local && (
      <span className="error-message">{errors.taxiNonWorking.local}</span>
    )}
  </div>

  <div className="service-input-box">
    <span>{t("opdSubsidized")}</span>
    <div className="znf-money">
      <span>₫</span>
      <input
        type="text"
        name="subsidized"
        value={taxiSame ? taxiWorking.subsidized : taxiNonWorking.subsidized}
        onChange={handleMoneyChange("taxiNonWorking", setTaxiNonWorking)}
        disabled={taxiSame}
      />
    </div>
    {!taxiSame && errors?.taxiNonWorking?.subsidized && (
      <span className="error-message">{errors.taxiNonWorking.subsidized}</span>
    )}
  </div>
</div>
          </div>
        </div>

        <div className="save-speciality znf-actions">
          <button
            type="button"
            className="form-section-cancel"
            onClick={() => nav(strings.navigation.manageTranspotationFees)}
          >
            {t("cancel")}
          </button>
          <button
            type="button"
            className={`form-section-create ${!isChanged ? "znf-disabled" : ""}`}
            disabled={!isChanged}
            onClick={handleSubmit}
          >
            {t("save")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditZoneFee;